// public/v3/src/lib/push-emr.js — verbatim port of v2's billing-note push
// pipeline, used by the v3 one-button "Push to EMR" workflow.
//
// This module exports `pushBillingNoteToElation(patient, monthStr, options)` — the
// v2-faithful equivalent of `pushBillingNoteForPatient(patientId)` — plus
// the helpers that compose into it. The single-button orchestrator
// (`pushPatientToEmr`) that wires generation → push → mark-billed is added
// in a follow-up commit; keeping the push primitive here lets it stay byte-
// identical to v2 even if the orchestrator gets reorganized later.
//
// Sources (v2 line numbers in public/ccm-compliance.js):
//   - pushBillingNoteForPatient            → 7343-7417
//   - _pushManyToElation                   → 7082-7124
//   - _collapseCptsForBill                 → 6916-6928
//   - _bnKeyToCpt                          → 6932-6939
//   - getElationBillingServiceDate         → 6949-6972
//   - clampServiceDateToToday              → 6974-6977
//   - defaultLastDayOfMonth                → 6941-6947
//   - getCcmBillingCodes                   → 6317-6339
//   - BN_COMBINED_KEY                      → 6643
//   - BILLING_TIME_TARGETS                 → 7830-7849
//   - CCM_TIERS keys                       → 6258-6277
//   - DEFAULT_RATES (descriptions)         → 7784-7827
//   - ADDON_CONFIG                         → 1830-1839
//   - getMonthlyActivitySummary (total)    → 6289-6314
//
// v2 → v3 adaptations (allowed-change list only):
//   - getPatient(id) global → patient passed in by caller
//   - _fetchWithTimeout(API_BASE+url, opts) → api.post('/data/elation/push-careplan', body)
//   - practiceSettings.elationPhysicianId → ccmApi.getSettings().practiceSettings.elationPhysicianId
//   - elationBilling global → loaded via ccmApi.getSettings()
//   - billedState global + saveBilledState() → ccmApi.getBilledState/saveBilledState
//     (GET-merge-POST so a single write doesn't clobber other months)
//   - toast() → returned in the result object for the caller's UI
//   - DOM progress bar updates (_pushManyToElation's btn arg) → removed; the
//     v3 caller drives progress via React state
//   - bnHistory cache lookup → on-the-fly fetch of /data/careplans?type=billing_notes
//     (the v3 generate flow writes to the same server table)
//   - pushMonthlyActivitiesAlongsideBillingNote inline call → pushMonthForPatient
//     in lib/push-month.js with options.alignDate=servicedAt so the phone
//     note's chart date matches the billing note's (mirrors v2:7404)
//   - confirm()/window.prompt() inside getElationBillingServiceDate → kept
//     for custom_prompt mode; a cancel returns null and aborts the push
//     (matches v2 behavior at line 6965-6966)

import { api, ccmApi } from './api';
import { pushMonthForPatient } from './push-month';
import { generateAndSaveCarePlan, getCarePlanFreshness } from './careplan-generate';
import { generateAndSaveBillingNote, generateAndSaveBhiBillingNote } from './billing-generate';
import { effectiveCcmCodeForMonth, isTierBumpCandidate, physicianTimePct, resolveAffirmedTier } from './ccm-tier-bump';
import { invalidateDraftCache } from './draft';
import { chronicCount } from './ai-suggest';
// BHI helpers live in a CommonJS sibling so Jest can require them directly.
// We import the pure resolve/gates and the BHI-specific bn key from there.
import { BHI_BN_KEY, resolveBhiBillCode, bhiGatesForCode, entryProgramFamily } from './bhi-billing';
import { isPainEnrolledPatient, painGatesForMonth, resolvePainCodes } from './pain-billing';
import { isPinEnrolledPatient, pinGatesForMonth, resolvePinCodes } from './pin-billing';
import { billingPushSlotKey, reconciledPushState } from './push-reconcile';
import { safeAudit } from './consent-audit';
import { payerProgramBlocker } from './payer-rules';

// ── Verbatim v2 constants ───────────────────────────────────────────────────

// v2 source: BN_COMBINED_KEY (ccm-compliance.js:6643)
export const BN_COMBINED_KEY = 'bn_combined';

// v2 source: ADDON_CONFIG (ccm-compliance.js:1830-1839)
const ADDON_CONFIG = [
  { base: '99490', addonCode: '99439', max: 2 },
  { base: '99491', addonCode: '99437', max: 2 },
  { base: '99487', addonCode: '99489', max: Infinity }, // CMS publishes no monthly unit cap for 99489
  { base: '99492', addonCode: '99494', max: 4 },
  { base: '99493', addonCode: '99494', max: 4 },
  { base: 'G0023', addonCode: 'G0024', max: 4 },
  { base: 'G3002', addonCode: 'G3003', max: 4 },
  { base: '99424', addonCode: '99425', max: 2 },
  { base: '99426', addonCode: '99427', max: 2 },
];

// v2 source: DEFAULT_RATES — only descriptions used by _bnKeyToCpt and
// _collapseCptsForBill labels. The full schedule lives in billing-generate.
const RATE_DESCS = {
  '99490': 'CCM Staff, 1st 20 min/month',
  '99439': 'CCM Staff, each addl 20 min',
  '99491': 'CCM Physician, 1st 30 min/month',
  '99437': 'CCM Physician, each addl 30 min',
  '99487': 'Complex CCM, 1st 60 min/month',
  '99489': 'Complex CCM, each addl 30 min',
  'G0556': 'APCM Level 1 (0-1 chronic conditions)',
  'G0557': 'APCM Level 2 (2+ chronic conditions)',
  'G0558': 'APCM Level 3 (QMB-eligible, 2+ chronic conditions)',
  'G0570': 'APCM-paired BHI Care Mgmt, per calendar month',
  '99484': 'BHI Care Mgmt, per calendar month',
  '99424': 'PCM Physician, 1st 30 min/month',
  '99425': 'PCM Physician, each addl 30 min',
  '99426': 'PCM Staff, 1st 30 min/month',
  '99427': 'PCM Staff, each addl 30 min',
  'G0023': 'Principal Illness Nav, 1st 60 min/month',
  'G0024': 'Principal Illness Nav, each addl 30 min',
  'G3002': 'Chronic Pain Mgmt, 1st 30 min/month',
  'G3003': 'Chronic Pain Mgmt, each addl 15 min',
};

// v2 source: BILLING_TIME_TARGETS (ccm-compliance.js:7830-7849)
export const BILLING_TIME_TARGETS = {
  '99490': 20, '99491': 30, '99487': 60,
  '99439': 20, '99437': 30, '99489': 30,
  'G0556': 0, 'G0557': 0, 'G0558': 0, 'G0570': 0,
  '99484': 20, '99492': 70, '99493': 60,
  'G2214': 30, 'G0568': 70, 'G0569': 60,
  '99494': 30,
  'G3002': 30, 'G3003': 15,
  '99424': 30, '99426': 30, '99425': 30, '99427': 30,
  '99457': 20, '98980': 20,
  'G0023': 60, 'G0140': 60, 'G0024': 30, 'G0146': 30,
};

// v2 source: CCM_TIERS keys (only the recognized base-program codes matter
// for getCcmBillingCodes — full descriptors live in billing-generate.js).
const CCM_TIERS_KEYS = new Set([
  '99490', '99491', '99487',
  'G0556', 'G0557', 'G0558',
  '99424', '99426', // PCM base codes — add-ons resolve via ADDON_CONFIG
]);
// CCM tier codes proper (pain pairs with these + APCM, never PCM).
const CCM_PROGRAM_TIER_CODES = new Set(['99490', '99491', '99487']);

// ── Helpers (verbatim v2 ports) ─────────────────────────────────────────────

// v2 source: getCcmBillingCodes (ccm-compliance.js:6317-6339).
// Returns the CPT list a patient bills for `totalMinutes`. CCM tiers always
// emit the base code (with `met` indicating whether the time threshold is
// satisfied); APCM tiers have target 0 and are always met.
//
// `opts.monthStr` (4th arg) enables same-bill family pairings. This must mirror
// billing-generate.js so the push payload's CPT list matches the generated note;
// opts._painScaleAcknowledged carries only the explicit Studio soft override.
export function getCcmBillingCodes(patient, totalMinutes, overrideBaseCode, opts) {
  const b = (patient && patient.billing) || {};
  const baseCode = overrideBaseCode || b.program;
  if (!baseCode || baseCode === 'none' || !CCM_TIERS_KEYS.has(baseCode)) return [];
  const target = BILLING_TIME_TARGETS[baseCode] != null ? BILLING_TIME_TARGETS[baseCode] : 20;
  const desc = RATE_DESCS[baseCode] || baseCode;
  const codes = [{ code: baseCode, desc, target, met: totalMinutes >= target }];
  const isApcm = baseCode === 'G0556' || baseCode === 'G0557' || baseCode === 'G0558';
  // CPM pairing — MUST mirror billing-generate.js exactly (the push payload's
  // CPT list and the generated note's CPT list may not diverge). Pain pairs
  // with CCM tiers and APCM bases only, never PCM.
  const painEligibleBase = isApcm || CCM_PROGRAM_TIER_CODES.has(baseCode);
  function _appendPainPairing() {
    const monthStr = opts && opts.monthStr;
    if (!monthStr || !painEligibleBase || !isPainEnrolledPatient(patient)) return;
    const gates = painGatesForMonth(patient, monthStr, opts && opts.staffList,
      opts && { scaleOverride: opts._painScaleAcknowledged === true });
    if (gates.ok) {
      resolvePainCodes(patient, monthStr, RATE_DESCS, opts && opts.staffList).forEach((c) => codes.push(c));
    }
  }
  const pinEligibleBase = isApcm || CCM_PROGRAM_TIER_CODES.has(baseCode);
  function _appendPinPairing() {
    const monthStr = opts && opts.monthStr;
    if (!monthStr || !pinEligibleBase || !isPinEnrolledPatient(patient)) return;
    const gates = pinGatesForMonth(patient, monthStr);
    if (gates.ok) {
      resolvePinCodes(patient, monthStr, RATE_DESCS).forEach((c) => codes.push(c));
    }
  }
  // APCM has no CCM-tier add-on configuration. APCM CAN pair with G0570 (BHI
  // add-on) when the patient is BHI-enrolled and BHI gates pass for the month.
  if (isApcm) {
    const monthStr = opts && opts.monthStr;
    if (monthStr) {
      const gates = bhiGatesForCode(patient, monthStr, 'G0570');
      if (gates.ok) {
        const g0570Desc = RATE_DESCS['G0570'] || 'APCM-paired BHI Care Mgmt, per calendar month';
        codes.push({ code: 'G0570', unit: 1, desc: g0570Desc, target: 0, met: true });
      }
    }
    _appendPinPairing();
    _appendPainPairing();
    return codes;
  }

  let cfg = null;
  for (let i = 0; i < ADDON_CONFIG.length; i++) {
    if (ADDON_CONFIG[i].base === baseCode) { cfg = ADDON_CONFIG[i]; break; }
  }
  if (cfg && totalMinutes > target) {
    const overflow = totalMinutes - target;
    const perUnit = BILLING_TIME_TARGETS[cfg.addonCode] || target;
    const units = Math.min(Math.floor(overflow / perUnit), cfg.max);
    for (let u = 1; u <= units; u++) {
      const aoDesc = RATE_DESCS[cfg.addonCode] || cfg.addonCode;
      codes.push({ code: cfg.addonCode, unit: u, desc: aoDesc, target: perUnit, met: true });
    }
  }
  _appendPinPairing();
  _appendPainPairing();
  return codes;
}

// v2 source: _collapseCptsForBill (ccm-compliance.js:6916-6928).
// Collapses a CPT list into the array shape the push route expects: one entry
// per unique CPT with summed units.
export function _collapseCptsForBill(cptList) {
  const byCode = {};
  const order = [];
  (cptList || []).forEach((ci) => {
    if (!ci || !ci.code) return;
    if (!byCode[ci.code]) {
      byCode[ci.code] = { cptCode: ci.code, cptDescription: ci.desc || '', units: 0 };
      order.push(ci.code);
    }
    byCode[ci.code].units += 1;
  });
  return order.map((c) => byCode[c]);
}

// Pre-push revalidation of a saved note's G3002 pairing (CPM P0.3). The note's
// "CPT Codes:" line is a snapshot from generation time — the CPM consent could
// have been revoked, the record found incomplete, or designated-provider
// enforcement could have flipped since. A stale G3002 must never reach the
// bill — BUT the persisted combined note TEXT asserts G3002 and is signed
// as-is, so silently stripping G3002 from the /bills/ payload would make the
// claim disagree with the signed note (codex re-review). Instead FAIL CLOSED:
// re-run TODAY's pain gates and, when they fail, report that the push must be
// BLOCKED until the note is regenerated against current gates. Pure so Jest
// can exercise it directly.
export function _revalidatePainCptsForPush(cptsForBill, patient, monthStr, staffList, scaleOverride) {
  const list = Array.isArray(cptsForBill) ? cptsForBill : [];
  // G3003 alone (e.g. a hand-edited CPT line) is still CPM billing and must
  // face the same gates — the add-on can never be more permissive than G3002.
  if (!list.some((c) => c && (c.cptCode === 'G3002' || c.cptCode === 'G3003'))) return { blocked: false, blockers: [] };
  const gates = painGatesForMonth(patient, monthStr, staffList, { scaleOverride: scaleOverride === true });
  if (gates.ok) return { blocked: false, blockers: [] };
  return { blocked: true, blockers: gates.blockers || [] };
}

// Fail closed when a saved note's PIN pairing no longer passes current gates;
// the signed combined note and pushed CPT list must remain identical.
export function _revalidatePinCptsForPush(cptsForBill, patient, monthStr, staffList) {
  const list = Array.isArray(cptsForBill) ? cptsForBill : [];
  if (!list.some((c) => c && c.cptCode === 'G0023')) return { blocked: false, blockers: [] };
  const gates = pinGatesForMonth(patient, monthStr);
  if (gates.ok) return { blocked: false, blockers: [] };
  return { blocked: true, blockers: gates.blockers || [] };
}

// Parse the authoritative "CPT Codes:" line out of a generation-time billing
// note into the /bills/ payload shape ({ cptCode, cptDescription, units }).
// The note text is the persisted record of what was generated AND what the
// provider reads and signs, so billing exactly this guarantees the bill cannot
// silently diverge from the signed note (audit #9) the way a live recompute can
// after a program/minutes change. Returns [] when the line is absent or
// unparseable so the caller can fall back to a live recompute. Line format
// (assembleCombinedBillingNote): "CPT Codes: 99490 + 99439 ×2".
export function _cptsFromNoteText(noteText) {
  if (!noteText || typeof noteText !== 'string') return [];
  const m = noteText.match(/^CPT Codes:\s*(.+)$/mi);
  if (!m) return [];
  const out = [];
  m[1].split('+').forEach((part) => {
    const s = String(part || '').trim();
    if (!s) return;
    const toks = s.split(/\s+/);
    const code = toks[0];
    if (!code) return;
    let units = 1;
    const unit = toks.slice(1).join(' ').match(/[x×]\s*(\d+)/i);
    if (unit) units = parseInt(unit[1], 10) || 1;
    out.push({ cptCode: code, cptDescription: RATE_DESCS[code] || '', units });
  });
  return out;
}

// v2 source: _bnKeyToCpt (ccm-compliance.js:6932-6939).
// Decodes legacy per-CPT-key archive keys (e.g. bn_99490, bn_99439_1).
export function _bnKeyToCpt(bnKey) {
  if (!bnKey || typeof bnKey !== 'string') return null;
  const m = bnKey.match(/^bn_([A-Z0-9]+)(?:_\d+)?$/i);
  if (!m) return null;
  const code = m[1];
  return { cptCode: code, cptDescription: RATE_DESCS[code] || '' };
}

// v2 source: defaultLastDayOfMonth (ccm-compliance.js:6941-6947)
function defaultLastDayOfMonth(monthStr) {
  const parts = String(monthStr || '').split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m) return new Date().toISOString().slice(0, 10);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

// v2 source: clampServiceDateToToday (ccm-compliance.js:6974-6977)
function clampServiceDateToToday(dateStr) {
  const today = new Date().toISOString().slice(0, 10);
  return (dateStr && dateStr > today) ? today : dateStr;
}

// v2 source: getElationBillingServiceDate (ccm-compliance.js:6949-6972).
// elationBilling lives in /data/settings.elationBilling on v3 (v2 keeps it
// in a top-level global). custom_prompt mode preserves the inline
// window.prompt — there's no clean way to surface this from a bulk flow,
// but single-patient pushes will see the same prompt v2 shows.
export async function getElationBillingServiceDate(monthStr, elationBilling) {
  const mode = ((elationBilling || {}).serviceDateMode) || 'billing_month_last_day';
  const today = new Date().toISOString().slice(0, 10);
  if (mode === 'today') return today;
  if (mode === 'billing_month_first_day') {
    const firstDay = monthStr + '-01';
    return firstDay > today ? today : firstDay;
  }
  if (mode === 'custom_prompt') {
    const entered = (typeof window !== 'undefined' && typeof window.prompt === 'function')
      ? window.prompt(
          'Service date for Elation billing note (YYYY-MM-DD):',
          clampServiceDateToToday(defaultLastDayOfMonth(monthStr))
        )
      : null;
    if (!entered) return null;
    return String(entered).trim();
  }
  // Default: last day of month, clamped to today.
  return clampServiceDateToToday(defaultLastDayOfMonth(monthStr));
}

// v2 source: _pushManyToElation (ccm-compliance.js:7082-7124).
// Sequential per-job push so partial failures can surface and progress
// notifications fire in order. api.post throws on non-ok; we catch and
// accumulate failures the same way v2 does.
async function _pushManyToElation(jobs, physicianId, onJobProgress) {
  let pushed = 0;
  const failures = [];
  const jobResults = [];
  for (let i = 0; i < jobs.length; i++) {
    const j = jobs[i];
    if (onJobProgress) onJobProgress({ index: i, total: jobs.length, label: j.label });
    let ok = false;
    let visitNoteId = null;
    let referralsDrafted = 0;
    try {
      const resp = await api.post('/data/elation/push-careplan', {
        elationId: j.elationId,
        planText: j.planText,
        physicianId: j.physicianId || physicianId || undefined,
        month: j.month,
        cptCode: j.cptCode,
        cptDescription: j.cptDescription,
        cpts: j.cpts,
        servicedAt: j.servicedAt,
        program: j.program,
      });
      pushed++;
      ok = true;
      visitNoteId = (resp && resp.visitNoteId) || null;
      referralsDrafted = (resp && resp.referralsDrafted) || 0;
    } catch (e) {
      // api.post throws Error with .status when server rejected; no .status
      // when fetch itself failed. Match v2's failure-message shape:
      // `${label}: ${err.error || 'HTTP ' + status}` or
      // `${label}: ${e.message || 'network error'}`.
      const serverErr = e && e.body && e.body.error;
      const status = e && typeof e.status === 'number' ? e.status : null;
      const msg = serverErr || (status ? 'HTTP ' + status : (e && e.message) || 'network error');
      failures.push(j.label + ': ' + msg);
    }
    jobResults.push({ job: j, ok, visitNoteId, referralsDrafted: ok ? referralsDrafted : 0 });
  }
  return { pushed, failures, aborted: false, jobResults };
}

// Load existing billing notes for one (patient, month). Returns the map
// stored by the server: { bn_combined: text } | { bn_99490: text, ... } | null.
// Replaces v2's `_bnHistory[p.id][monthStr]` cache read.
async function _getMonthBillingNotes(patientId, monthStr) {
  try {
    const data = await ccmApi.listBillingNotes();
    const bn = (data && data.billing_notes) ? data.billing_notes : (data || {});
    const monthMap = bn[patientId] && bn[patientId][monthStr];
    return monthMap || null;
  } catch (_) {
    return null;
  }
}

// Load practice settings (physicianId + elationBilling). Cached for the
// duration of one push so we don't refetch per job.
let _settingsCache = null;
async function _getSettings() {
  if (_settingsCache) return _settingsCache;
  try {
    const s = await ccmApi.getSettings();
    _settingsCache = {
      physicianId: (s && s.practiceSettings && s.practiceSettings.elationPhysicianId) || '',
      elationBilling: (s && s.elationBilling) || { serviceDateMode: 'billing_month_last_day' },
    };
  } catch (_) {
    _settingsCache = { physicianId: '', elationBilling: { serviceDateMode: 'billing_month_last_day' } };
  }
  return _settingsCache;
}
export function _resetPushEmrSettingsCache() { _settingsCache = null; }

// Reconcile a reported push failure against the server's billingPush slot.
// Returns 'pushed' (the bill is in Elation — the failure was a false positive),
// 'bill_failed' (an orphaned note is in Elation, needs manual resolution), or
// null (no evidence it landed → genuine failure). A read-only check: it can
// only ever downgrade a "failed" message to a more accurate one, never the
// reverse. Best-effort — a fetch error returns null (fall back to "failed").
export async function checkBillLanded(patientId, monthStr, program) {
  if (!patientId || !monthStr) return null;
  try {
    const fresh = await ccmApi.getPatient(patientId);
    const slot = fresh && fresh.billingPush && fresh.billingPush[billingPushSlotKey(monthStr, program)];
    return reconciledPushState(slot);
  } catch (_) {
    return null;
  }
}

// Sum the month's logged minutes from a patient's activityLog. Mirrors v2's
// getMonthlyActivitySummary(p.id, monthStr).total (ccm-compliance.js:6289-6314).
export function monthlyMinutes(rawPatient, monthStr) {
  const log = Array.isArray(rawPatient.activityLog) ? rawPatient.activityLog : [];
  let total = 0;
  for (const e of log) {
    if (!e || e.isConsolidatedNote) continue;
    if ((e.date || '').indexOf(monthStr) !== 0) continue;
    total += parseInt(e.duration, 10) || parseInt(e.mins, 10) || 0;
  }
  return total;
}

// CCM-family minutes only — excludes BHI-coded entries (99484/G0570) so a
// dually CCM+BHI-enrolled patient's BHI time never inflates the CCM bill's
// add-on units or satisfies the CCM time gate. This MUST match the basis the
// billing NOTE uses (billing-generate.js getMonthlyActivitySummary(p, m, 'ccm')),
// or the signed note and the submitted bill diverge invisibly. See entryProgramFamily
// (bhi-billing.js) — the single source of truth for activity-log family.
export function ccmMonthlyMinutes(rawPatient, monthStr) {
  const log = Array.isArray(rawPatient.activityLog) ? rawPatient.activityLog : [];
  let total = 0;
  for (const e of log) {
    if (!e || e.isConsolidatedNote) continue;
    if ((e.date || '').indexOf(monthStr) !== 0) continue;
    if (entryProgramFamily(e) !== 'ccm') continue;
    total += parseInt(e.duration, 10) || parseInt(e.mins, 10) || 0;
  }
  return total;
}

export function hasDocumentedMonthlyActivity(rawPatient, monthStr) {
  const log = Array.isArray(rawPatient.activityLog) ? rawPatient.activityLog : [];
  return log.some((e) => {
    if (!e || e.isConsolidatedNote) return false;
    return (e.date || '').indexOf(monthStr) === 0;
  });
}

function _desiredBaseCodeForMonth(rawPatient, monthStr) {
  const raw = rawPatient || {};
  const minutes = ccmMonthlyMinutes(raw, monthStr);
  return effectiveCcmCodeForMonth(raw, monthStr, minutes) || '';
}

export function isApcmDecisionCompatible(rawPatient, code) {
  const raw = rawPatient || {};
  const n = chronicCount({ _raw: raw });
  const qmbConfirmed = !!(raw.qmb && raw.qmb.confirmed);
  if (code === 'G0556') return n < 2;
  if (code === 'G0557') return n >= 2;
  if (code === 'G0558') return n >= 2 && qmbConfirmed;
  return false;
}

function apcmDecisionIncompatibilityReason(rawPatient, code) {
  if (code === 'G0556') return 'APCM G0556 requires 0-1 chronic conditions';
  if (code === 'G0557') return 'APCM G0557 requires 2+ chronic conditions';
  if (code === 'G0558') return 'APCM G0558 requires confirmed QMB and 2+ chronic conditions';
  return 'APCM code is not recognized';
}

function _extractBillingNoteCptCodes(noteText) {
  if (!noteText || typeof noteText !== 'string') return [];
  const match = noteText.match(/^CPT Codes:\s*(.+)$/mi);
  if (!match) return [];
  return match[1]
    .split('+')
    .map((part) => String(part || '').trim().split(/\s+/)[0])
    .filter(Boolean);
}

export function billingNotesMatchDesiredBaseCode(monthNotes, desiredCode) {
  const wanted = String(desiredCode || '').trim();
  if (!wanted || !monthNotes) return false;
  if (typeof monthNotes === 'string') {
    return _extractBillingNoteCptCodes(monthNotes).includes(wanted);
  }
  if (typeof monthNotes !== 'object') return false;

  if (monthNotes[BN_COMBINED_KEY]) {
    return _extractBillingNoteCptCodes(monthNotes[BN_COMBINED_KEY]).includes(wanted);
  }

  return Object.keys(monthNotes).some((k) => {
    if (!monthNotes[k]) return false;
    const cpt = _bnKeyToCpt(k);
    return !!(cpt && cpt.cptCode === wanted);
  });
}

function _isApcmBillingCode(code) {
  return ['G0556', 'G0557', 'G0558'].includes(String(code || '').trim());
}

function _billingNoteHasDeprecatedApcmActivityLanguage(noteText) {
  if (!noteText || typeof noteText !== 'string') return false;
  return /APCM elected for longitudinal primary care management this billing period/i.test(noteText)
    || /Patient enrolled in Advanced Primary Care Management/i.test(noteText)
    || /flat per-month (payment|service)/i.test(noteText)
    || /distinct from time-based CCM/i.test(noteText)
    || /\nGOALS:\s*\n/i.test(noteText)
    || /no monthly activity entries were logged/i.test(noteText)
    || /TIME DOCUMENTED THIS MONTH:\s*0\s*minutes/i.test(noteText)
    || /-\s*G055[678]:\s*0\s*min/i.test(noteText)
    || /APCM is a flat-fee program; minutes are documented/i.test(noteText)
    || /Advanced Primary Care Management enrollment were satisfied, including completion of a qualifying initiating visit where required/i.test(noteText);
}

function _billingNoteHasDeprecatedCcmQualityLanguage(noteText) {
  if (!noteText || typeof noteText !== 'string') return false;
  return /\(staff missing\)/i.test(noteText)
    || /entry notes do not contain additional specifics/i.test(noteText)
    || /notes do not contain additional specifics beyond scope/i.test(noteText)
    || /documentation does not contain additional specifics/i.test(noteText);
}

export function billingNotesNeedRegeneration(monthNotes, desiredCode) {
  if (!monthNotes) return false;
  const checkNote = (noteText) => {
    if (_billingNoteHasDeprecatedCcmQualityLanguage(noteText)) return true;
    return _isApcmBillingCode(desiredCode) && _billingNoteHasDeprecatedApcmActivityLanguage(noteText);
  };
  if (typeof monthNotes === 'string') return checkNote(monthNotes);
  if (typeof monthNotes !== 'object') return false;
  return Object.values(monthNotes).some(checkNote);
}

// ── Public API ──────────────────────────────────────────────────────────────

// Verbatim port of v2's pushBillingNoteForPatient (ccm-compliance.js:7343-7417).
// Pushes the saved billing note for one (patient, month) to Elation as one or
// more visit notes (one per CPT for legacy data, one combined for new data),
// then auto-pushes the consolidated monthly phone note dated the same as the
// billing note's servicedAt. Returns:
//
//   { ok: bool,
//     pushed: number,      // billing-note jobs successfully pushed
//     total: number,       // billing-note jobs attempted
//     failures: string[],  // billing-note failure strings, v2 format
//     servicedAt: string|null,  // YYYY-MM-DD used for billing-note + phone-note
//     phoneNote: { ok, pushed, failed, entries } | null,
//     visitNoteIds: string[] }
//
// Errors short-circuit (return ok:false) only when the prerequisites fail:
// patient unlinked, no billing note saved, no jobs constructible, or
// servicedAt cancelled. A partial-success billing push (some jobs failed)
// returns ok:false too so the caller can re-attempt without marking billed.
// options._painScaleAcknowledged preserves an explicit missing-scale decision
// through the live-CPT fallback and the mandatory G3002 revalidation.
export async function pushBillingNoteToElation(patient, monthStr, options) {
  const opts = options || {};
  const scaleOverride = opts._painScaleAcknowledged === true;
  const raw = patient?._raw || patient || {};
  const patientId = patient?.id || raw.id;
  if (!raw.elation_id) {
    return { ok: false, pushed: 0, total: 0, failures: ['not linked to Elation'], servicedAt: null, phoneNote: null, visitNoteIds: [] };
  }
  const monthNotes = await _getMonthBillingNotes(patientId, monthStr);
  if (!monthNotes || !Object.keys(monthNotes).length) {
    return { ok: false, pushed: 0, total: 0, failures: ['No billing note saved for this month — generate one first.'], servicedAt: null, phoneNote: null, visitNoteIds: [] };
  }

  const name = (raw.fname || '') + ' ' + (raw.lname || '');
  const jobs = [];
  if (typeof monthNotes === 'string') {
    jobs.push({ elationId: raw.elation_id, planText: monthNotes, month: monthStr, label: name + ' (billing_note)' });
  } else if (monthNotes[BN_COMBINED_KEY]) {
    // New combined shape: one push, all CPTs on one bill.
    // If an APCM tier was recorded for this month, force the G-code as the
    // bill code instead of the patient's CCM program.
    const apcmMap = (raw.apcmMonth && typeof raw.apcmMonth === 'object') ? raw.apcmMonth : {};
    const apcmDecision = apcmMap[monthStr] || null;
    if (apcmDecision && apcmDecision.code && !isApcmDecisionCompatible(raw, String(apcmDecision.code))) {
      return {
        ok: false,
        pushed: 0,
        total: 0,
        failures: [apcmDecisionIncompatibilityReason(raw, String(apcmDecision.code))],
        servicedAt: null,
        phoneNote: null,
        visitNoteIds: [],
      };
    }
    // Bill exactly what the generation-time note prints (audit #9): the note's
    // "CPT Codes:" line is the persisted record the provider reads and signs, so
    // deriving the /bills/ payload from it — rather than recomputing live —
    // guarantees the bill can't silently diverge from the signed note after a
    // program/minutes change between generate and push.
    let cptsForBill = _cptsFromNoteText(monthNotes[BN_COMBINED_KEY]);
    if (!cptsForBill.length) {
      // Fallback: note has no parseable CPT line. Recompute from CCM-family
      // minutes only (audit #1/#2) — must match the note's generation basis
      // (getMonthlyActivitySummary(p, m, 'ccm')); unfiltered minutes would let a
      // dual patient's BHI time inflate the CCM add-on units. Pass `{ monthStr }`
      // so APCM months with BHI gates satisfied get G0570 on the bill.
      const totalForPush = ccmMonthlyMinutes(raw, monthStr);
      const cptList = getCcmBillingCodes(raw, totalForPush, effectiveCcmCodeForMonth(raw, monthStr, totalForPush), {
        monthStr, auditOnDrop: true, _painScaleAcknowledged: scaleOverride,
      });
      cptsForBill = _collapseCptsForBill(cptList);
    }
    // PIN pre-push revalidation: the combined note and claim must stay paired.
    if (cptsForBill.some((c) => c && c.cptCode === 'G0023')) {
      const staffList = [];
      const reval = _revalidatePinCptsForPush(cptsForBill, raw, monthStr, staffList);
      if (reval.blocked) {
        safeAudit('PIN_G0023_DROPPED', patientId, 'push blocked pending regeneration: ' + reval.blockers.join('; '));
        return { ok: false, pushed: 0, total: 0, failures: ['PIN gates no longer pass for this note — regenerate before pushing (blockers: ' + reval.blockers.join('; ') + ')'], servicedAt: null, phoneNote: null, visitNoteIds: [] };
      }
    }
    // CPM pre-push revalidation (P0.3): a stale G3002 must never reach the bill
    // OR ride the signed note. staffList is fetched only when the note actually
    // carries G3002 (a fetch failure means unstamped legacy entries resolve to
    // unknown credential — fail-closed, the compliant direction). The recompute
    // fallback above just ran the gates, so this re-check is a no-op there by
    // construction; the block below only bites a G3002 parsed from the note.
    if (cptsForBill.some((c) => c && (c.cptCode === 'G3002' || c.cptCode === 'G3003'))) {
      let staffList = [];
      try { staffList = await ccmApi.listStaff(); } catch (_) { staffList = []; }
      const reval = _revalidatePainCptsForPush(cptsForBill, raw, monthStr, staffList, scaleOverride);
      if (reval.blocked) {
        // FAIL-CLOSED BLOCK (codex re-review): the combined note carries every
        // CPT in ONE signed text. Stripping G3002 from cptsForBill while pushing
        // the unchanged planText would bill 99490 but sign a note asserting
        // G3002 — the bill and the signed note must never disagree. Block this
        // patient entirely and require regeneration against today's gates.
        safeAudit('PAIN_G3002_DROPPED', patientId, 'push blocked pending regeneration: ' + reval.blockers.join('; '));
        return { ok: false, pushed: 0, total: 0, failures: ['CPM gates no longer pass for this note — regenerate before pushing (blockers: ' + reval.blockers.join('; ') + ')'], servicedAt: null, phoneNote: null, visitNoteIds: [] };
      }
    }
    jobs.push({
      elationId: raw.elation_id,
      planText: monthNotes[BN_COMBINED_KEY],
      month: monthStr,
      label: name + ' (' + cptsForBill.map((c) => c.cptCode + (c.units > 1 ? '×' + c.units : '')).join('+') + ')',
      cpts: cptsForBill,
    });
  } else {
    // Legacy per-CPT-key shape (months saved before combined-note rollout).
    // Skip the BHI key — _bnKeyToCpt('bhi_bn') is null, so it would otherwise
    // push as a CPT-less care-plan note with no bill. BHI notes have their own
    // push path (_pushAndSignForPatientBhi).
    Object.keys(monthNotes).filter((k) => monthNotes[k] && k !== BHI_BN_KEY).forEach((k) => {
      const cpt = _bnKeyToCpt(k) || {};
      jobs.push({
        elationId: raw.elation_id,
        planText: monthNotes[k],
        month: monthStr,
        label: name + ' (' + k + ')',
        cptCode: cpt.cptCode,
        cptDescription: cpt.cptDescription,
      });
    });
    // PIN legacy keys carry one note per CPT, so failed gates remove the PIN
    // note jobs and their matching claim lines together.
    if (jobs.some((j) => j.cptCode === 'G0023' || j.cptCode === 'G0024')) {
      const gates = pinGatesForMonth(raw, monthStr);
      if (!gates.ok) {
        for (let i = jobs.length - 1; i >= 0; i--) {
          if (jobs[i].cptCode === 'G0023' || jobs[i].cptCode === 'G0024') jobs.splice(i, 1);
        }
        safeAudit('PIN_G0023_DROPPED', patientId, 'stale note revalidation (legacy keys): ' + (gates.blockers || []).join('; '));
      }
    }
    // CPM pre-push revalidation, legacy shape (codex 8c review): CPM shipped
    // after the combined-note rollout so bn_G3002/bn_G3003 keys shouldn't
    // exist, but _bnKeyToCpt would happily decode one — apply the same
    // fail-closed gate as the combined path. Dropping every job here falls
    // through to the !jobs.length guard below (push aborts, nothing billed).
    if (jobs.some((j) => j.cptCode === 'G3002' || j.cptCode === 'G3003')) {
      let staffList = [];
      try { staffList = await ccmApi.listStaff(); } catch (_) { staffList = []; }
      const gates = painGatesForMonth(raw, monthStr, staffList, { scaleOverride });
      if (!gates.ok) {
        for (let i = jobs.length - 1; i >= 0; i--) {
          if (jobs[i].cptCode === 'G3002' || jobs[i].cptCode === 'G3003') jobs.splice(i, 1);
        }
        safeAudit('PAIN_G3002_DROPPED', patientId, 'stale note revalidation (legacy keys): ' + (gates.blockers || []).join('; '));
      }
    }
  }
  if (!jobs.length) {
    return { ok: false, pushed: 0, total: 0, failures: ['No billing notes to push for ' + name + '.'], servicedAt: null, phoneNote: null, visitNoteIds: [] };
  }

  // Either path (single cptCode legacy, or combined cpts[] new) needs the
  // billing service date so the visit_note's document_date matches.
  let servicedAt = null;
  if (jobs.some((j) => !!j.cptCode || (Array.isArray(j.cpts) && j.cpts.length))) {
    const { elationBilling } = await _getSettings();
    servicedAt = await getElationBillingServiceDate(monthStr, elationBilling);
    if (!servicedAt) {
      return { ok: false, pushed: 0, total: jobs.length, failures: ['service date not selected (custom_prompt cancelled)'], servicedAt: null, phoneNote: null, visitNoteIds: [] };
    }
    jobs.forEach((j) => {
      if (j.cptCode || (Array.isArray(j.cpts) && j.cpts.length)) j.servicedAt = servicedAt;
    });
  }

  const { physicianId } = await _getSettings();
  const pushRes = await _pushManyToElation(jobs, physicianId);
  const visitNoteIds = pushRes.jobResults.filter((r) => r.ok && r.visitNoteId).map((r) => r.visitNoteId);
  const referralsDrafted = pushRes.jobResults.reduce((n, r) => n + (r.referralsDrafted || 0), 0);

  // Compute the alignDate the same way v2 line 7399 does: prefer servicedAt;
  // fall back to today when no cpt-coded jobs were sent.
  const pushDate = servicedAt || new Date().toISOString().slice(0, 10);

  // Phone-note push (mirrors v2:7404 inside the "every job pushed" branch).
  // Only fires when the billing push fully succeeded — otherwise we leave the
  // phone note for a re-attempt so the two chart entries stay in lockstep.
  let phoneNote = null;
  if (pushRes.pushed === jobs.length) {
    // The billing push's server-side idempotency claim writes billingPush into
    // the patient blob and bumps patients.updated_at. That makes the patient
    // snapshot supplied to this function stale before pushMonthForPatient's
    // full-blob PUT. Re-fetch so the phone-note write carries both the current
    // billingPush marker and a fresh optimistic-lock token. If the refresh
    // itself fails, keep the historic best-effort behavior: attempt the phone
    // note with the snapshot and report any resulting error as non-blocking.
    let phonePatient = patient;
    try {
      const fresh = await ccmApi.getPatient(patientId);
      if (fresh && fresh.id) phonePatient = { ...patient, id: patientId, _raw: fresh };
    } catch (_) { /* billing succeeded; the optional phone-note leg remains best-effort */ }
    try {
      const ph = await pushMonthForPatient(phonePatient, monthStr, { alignDate: pushDate });
      phoneNote = ph;
    } catch (e) {
      phoneNote = { ok: false, pushed: 0, failed: 1, entries: 0, error: e?.message || 'phone push failed' };
    }
  }

  // Surface the Elation non_visit_note id of the phone-note (a.k.a. activity
  // log) so the Bulk Sign / per-patient Sign buttons can include it alongside
  // the billing visit_notes. Skip the 'created' placeholder string the server
  // returns when it couldn't determine an id.
  const nonVisitNoteIds = (phoneNote && phoneNote.ok && Number.isInteger(phoneNote.noteId) && phoneNote.noteId > 0)
    ? [phoneNote.noteId]
    : [];

  return {
    ok: pushRes.pushed === jobs.length,
    pushed: pushRes.pushed,
    total: jobs.length,
    failures: pushRes.failures,
    servicedAt: pushDate,
    phoneNote,
    visitNoteIds,
    nonVisitNoteIds,
    referralsDrafted,
  };
}

// ── One-button orchestrator + eligibility ──────────────────────────────────
//
// Sources (v2 line numbers in public/ccm-compliance.js):
//   - per-row eligibility (consent/program/elation_id/billed/time-threshold)
//     mirrors the gates in pushBillingNoteForPatient (7344-7349) and
//     pushAllBillingReadyNotes (7135-7178) — both lift the same checks from
//     the workflow filter (getWorkflowPatients) and the billedState map.
//   - billedState write shape `{ [month]: { [patientId]: { date } } }`
//     matches v2 line 7400 / 7227.
//
// v2 → v3 adaptations (allowed-change list only):
//   - getPatient / billedState global → patient + billedState passed in
//   - confirm() / toast() → returned in the result object
//   - pushBillingNoteToElation already handles the multi-CPT push + phone
//     note + servicedAt; this layer only adds generation gates + billed-state
//     write + per-step UX callbacks.

// Load existing care plan for one (patient, month). Returns plan text or null.
async function _getMonthCarePlan(patientId, monthStr) {
  try {
    const data = await ccmApi.listCarePlans();
    const cp = (data && data.care_plan) ? data.care_plan : (data || {});
    const text = cp[patientId] && cp[patientId][monthStr];
    return text || null;
  } catch (_) { return null; }
}

// Same shape as _getMonthBillingNotes but exported for the orchestrator.
async function _getMonthBillingNotesOrch(patientId, monthStr) {
  try {
    const data = await ccmApi.listBillingNotes();
    const bn = (data && data.billing_notes) ? data.billing_notes : (data || {});
    const monthMap = bn[patientId] && bn[patientId][monthStr];
    return monthMap || null;
  } catch (_) { return null; }
}

// Merge a patient's billed entry into billedState and POST. v2 keeps
// billedState as a long-lived global; v3 has no such global so we GET → merge
// → POST each write. The server is the source of truth.
//
// `opts.program === 'bhi'` writes the BHI-specific key (`bhi_bn`) under the
// patient entry, preserving any existing CCM `date` field. Omitted/'ccm'
// writes the legacy CCM `date` field, preserving any existing `bhi_bn`
// subkey. The two program slots are independent — a patient may be
// CCM-billed but not BHI-billed (or vice versa) in the same month.
// The billed marker is the ONLY guard against re-pushing an already-filed
// bill to Elation (getPushEligibility → 'already submitted'). By the time we
// mark billed, the irreversible Elation push has already happened — so a
// transient failure persisting this marker would let the next run duplicate
// the bill. Retry the save before giving up.
const BILLED_SAVE_ATTEMPTS = 3;
const BILLED_SAVE_RETRY_MS = 400;

async function _markPatientBilled(patientId, monthStr, pushDate, opts) {
  const program = (opts && opts.program) || 'ccm';
  let current = {};
  try { current = await ccmApi.getBilledState(); } catch (_) {}
  const next = { ...(current || {}) };
  next[monthStr] = { ...(next[monthStr] || {}) };
  const existing = next[monthStr][patientId] || {};
  if (program === 'bhi') {
    next[monthStr][patientId] = { ...existing, bhi_bn: { date: pushDate } };
  } else {
    next[monthStr][patientId] = { ...existing, date: pushDate };
  }
  let lastErr;
  for (let attempt = 0; attempt < BILLED_SAVE_ATTEMPTS; attempt++) {
    try {
      await ccmApi.saveBilledState(next);
      return next;
    } catch (e) {
      lastErr = e;
      if (attempt < BILLED_SAVE_ATTEMPTS - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, BILLED_SAVE_RETRY_MS));
      }
    }
  }
  throw lastErr;
}

// Symmetric helper for the regenerate path: clear a patient's billed entry so
// they reappear in the "ready to Push & Sign" bucket. No-op if the entry is
// absent.
//
// `opts.program === 'bhi'` clears only the BHI subkey, preserving any
// existing CCM `date` field. Omitted/'ccm' clears the entire patient entry
// (legacy behavior — CCM regenerate cleared the whole row before BHI was
// introduced; we keep that for backwards compatibility but extend so BHI
// regenerate doesn't blow away the CCM mark).
async function _unmarkPatientBilled(patientId, monthStr, opts) {
  const program = (opts && opts.program) || 'ccm';
  let current = {};
  try { current = await ccmApi.getBilledState(); } catch (_) {}
  if (!current || !current[monthStr] || !current[monthStr][patientId]) return current;
  const next = { ...current, [monthStr]: { ...current[monthStr] } };
  if (program === 'bhi') {
    const existing = { ...next[monthStr][patientId] };
    delete existing.bhi_bn;
    // If the patient entry has no other keys (was BHI-only), drop it entirely.
    if (Object.keys(existing).length === 0) {
      delete next[monthStr][patientId];
    } else {
      next[monthStr][patientId] = existing;
    }
  } else {
    delete next[monthStr][patientId];
  }
  await ccmApi.saveBilledState(next);
  return next;
}

// Sign helper used by the atomic Push & Sign step. Calls the server's
// bulk-sign route once, retries once after 2s on failure (handles transient
// 5xx). Caller decides how to surface a final failure — the push has already
// succeeded by the time we get here, so the unsigned drafts simply remain in
// the Elation inbox for manual signing.
async function _signWithRetry({ noteIds, nonVisitNoteIds, reportIds }) {
  const payload = { noteIds: noteIds || [], nonVisitNoteIds: nonVisitNoteIds || [], reportIds: reportIds || [] };
  if (!payload.noteIds.length && !payload.nonVisitNoteIds.length && !payload.reportIds.length) {
    // Shape symmetry with the live bulk-sign response (which always returns
    // reportResults alongside results/nonVisitResults) so callers that read
    // signResult.reportResults don't have to special-case the empty path.
    return { signed: 0, failed: 0, results: [], nonVisitResults: [], reportResults: [] };
  }
  try {
    return await ccmApi.bulkSignVisitNotes(payload);
  } catch (e) {
    await new Promise((r) => setTimeout(r, 2000));
    return ccmApi.bulkSignVisitNotes(payload);
  }
}

// Compute eligibility for a single patient. Pure function — no network.
// Pass billedState (from ccmApi.getBilledState()) so multi-patient callers
// can fetch once.
//
// Order of checks mirrors v2's pushBillingNoteForPatient gates:
//   1. consent must be signed
//   2. program must be set (not 'none')
//   3. elation_id must be set
//   4. billedState[month][patientId] must not exist
//   5. time threshold per program (BILLING_TIME_TARGETS):
//      99490 → 20m, 99491 → 30m, 99487 → 60m,
//      APCM (G0556/G0557/G0558) → 0m (always pass)
//
// Returns { eligible: boolean, reason: string }.
//
// BHI overload: callers can pass `{ program: 'bhi' }` as the 3rd arg (no
// billedState) OR the 4th arg (with billedState). When `opts.program === 'bhi'`,
// the function returns the BHI-shaped result { ready, reason, code, blockers }
// instead of the CCM-shaped { eligible, reason }. This split shape mirrors
// bhiGatesForCode's return contract and is what BHI UI surfaces consume.
export function getPushEligibility(patient, monthStr, billedState, options) {
  // Overload: if the 3rd arg looks like an options bag (has `program` or
  // `ignoreAlreadySubmitted`) and not a billedState map, treat it as options.
  // billedState is shaped { [yyyy-mm]: { [patientId]: ... } } — never has
  // `program`/`ignoreAlreadySubmitted` keys, so the detection is unambiguous.
  let actualBilledState = billedState;
  let opts = options || {};
  if (billedState && (billedState.program != null || billedState.ignoreAlreadySubmitted != null)
      && Object.keys(billedState).every(function(k) { return k === 'program' || k === 'ignoreAlreadySubmitted'; })) {
    opts = billedState;
    actualBilledState = {};
  }

  // BHI branch — bypass CCM-shaped gates entirely and return the BHI result.
  // No billedState check here in Phase 1: BHI's billed-state tracking lands in
  // a later phase. UI gates already block double-pushes via getWorkflowState.
  if (opts.program === 'bhi') {
    const code = resolveBhiBillCode(patient, monthStr);
    // Thread the assessment-override soft guard: when set, a missing initial
    // assessment reads as a warning (surfaced on `warnings`) rather than a
    // blocker, so `ready` isn't held down by it alone. Callers that omit the
    // flag see the missing assessment as a blocker exactly as before.
    const gates = bhiGatesForCode(patient, monthStr, code, { assessmentOverride: !!opts.assessmentOverride });
    return {
      ready: gates.ok,
      reason: (gates.blockers && gates.blockers[0]) || '',
      code,
      blockers: gates.blockers || [],
      warnings: gates.warnings || [],
    };
  }

  const raw = patient?._raw || patient || {};
  const consent = raw.consent || 'pending';
  if (consent !== 'signed') {
    if (consent === 'declined') return { eligible: false, reason: 'consent declined' };
    return { eligible: false, reason: 'consent pending' };
  }
  const program = raw.billing?.program;
  if (!program || program === 'none') {
    return { eligible: false, reason: 'not enrolled' };
  }
  // Payer-program compatibility: getPushEligibility was fully payer-blind, so
  // a Medicaid-only patient enrolled (or bulk-enrolled) into a Medicare-only
  // code would push a denied-on-arrival claim. Mirrored server-side in the
  // push route — this copy exists to stop the operator before the push.
  const payerBlk = payerProgramBlocker(raw.payerClass, program);
  if (payerBlk) {
    return { eligible: false, reason: 'payer mismatch — ' + payerBlk };
  }
  if (!raw.elation_id) {
    return { eligible: false, reason: 'not linked to Elation' };
  }
  const bs = actualBilledState || {};
  const patientId = patient?.id || raw.id;
  if (!opts.ignoreAlreadySubmitted && bs[monthStr] && bs[monthStr][patientId]) {
    return { eligible: false, reason: 'already submitted' };
  }
  // APCM fallback branch: if the operator has recorded an APCM tier for this
  // month, the time threshold is met (APCM G-codes have target 0 — flat fee).
  // The bill will go out as the recorded G-code, not the CCM program code.
  const apcmMap = (raw.apcmMonth && typeof raw.apcmMonth === 'object') ? raw.apcmMonth : {};
  if (apcmMap[monthStr] && apcmMap[monthStr].code) {
    const code = String(apcmMap[monthStr].code);
    // The recorded APCM G-code is what actually bills — payer-check it too.
    const apcmPayerBlk = payerProgramBlocker(raw.payerClass, code);
    if (apcmPayerBlk) {
      return { eligible: false, reason: 'payer mismatch — ' + apcmPayerBlk };
    }
    if (!isApcmDecisionCompatible(raw, code)) {
      return { eligible: false, reason: apcmDecisionIncompatibilityReason(raw, code) };
    }
    return { eligible: true, reason: '' };
  }

  // Time threshold via BILLING_TIME_TARGETS so the rules track v2 exactly.
  // Unrecognized codes fall back to 20m (matches v2's `targets[code] || 20`).
  const target = BILLING_TIME_TARGETS[program] != null ? BILLING_TIME_TARGETS[program] : 20;
  if (target > 0) {
    // CCM-family minutes only (BHI branch already returned above). Counting
    // cross-program minutes here would let a dual patient's BHI time satisfy
    // the CCM time threshold for a month with sub-threshold documented CCM time.
    const logged = ccmMonthlyMinutes(raw, monthStr);
    if (logged < target) {
      return { eligible: false, reason: 'Short — ' + logged + 'm of ' + target + 'm' };
    }
  }
  return { eligible: true, reason: '' };
}

// One-button orchestrator. Steps run sequentially:
//   1. care plan (generate if missing)
//   2. billing note (generate if missing)
//   3. billing note push to Elation (multi-CPT via pushBillingNoteToElation —
//      which also auto-pushes the consolidated phone note with the matching
//      servicedAt)
//   4. billedState write
//
// options.onStep({ step, patientId }) — fires before each major step. step ∈
// { 'careplan', 'billing-note', 'billing-push', 'phone-push', 'billed-state' }.
// The phone-push step is reported synthetically based on the result of
// pushBillingNoteToElation's phoneNote field so the UI can render five rows.
//
// options.freshness — pre-computed getCarePlanFreshness() result (used by the
// 99487 pre-generation review path so generateAndSaveCarePlan doesn't refetch
// the same data the eligibility check already pulled).
//
// Returns:
//   { ok, skipped?, reason?, results: { carePlanGenerated, billingNoteGenerated,
//     billingPushed, phoneNotePushed, billedStateUpdated }, errors: [...] }
//
// Skipped (ineligible) calls return ok:true with skipped:true and reason set —
// the caller treats them as no-op success.
export async function pushPatientToEmr(patient, monthStr, options) {
  const opts = options || {};
  const onStep = typeof opts.onStep === 'function' ? opts.onStep : null;
  const raw = patient?._raw || patient || {};
  const patientId = patient?.id || raw.id;
  const results = {
    carePlanGenerated: false,
    billingNoteGenerated: false,
    billingPushed: false,
    phoneNotePushed: false,
    billedStateUpdated: false,
  };
  const errors = [];

  // Re-check eligibility right before running. The caller may have computed
  // it earlier with a stale billedState; refetch so a racing manual mark
  // from another tab doesn't cause a duplicate push.
  let billedState = {};
  try { billedState = await ccmApi.getBilledState(); } catch (_) {}
  const forceRerun = opts.forceRerun === true;
  const elig = getPushEligibility(patient, monthStr, billedState, { ignoreAlreadySubmitted: forceRerun });
  if (!elig.eligible) {
    return { ok: true, skipped: true, reason: elig.reason, results, errors, visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // ── Step 1: care plan ───────────────────────────────────────────────────
  if (onStep) onStep({ step: 'careplan', patientId });
  try {
    const existingPlan = await _getMonthCarePlan(patientId, monthStr);
    if (forceRerun || !existingPlan) {
      await generateAndSaveCarePlan(patient, monthStr,
        opts.freshness ? { freshness: opts.freshness } : undefined);
      results.carePlanGenerated = true;
    }
  } catch (e) {
    errors.push('care plan: ' + (e?.message || 'unknown error'));
    return { ok: false, results, errors, visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // ── Step 2: billing note ────────────────────────────────────────────────
  if (onStep) onStep({ step: 'billing-note', patientId });
  try {
    const existingNotes = await _getMonthBillingNotesOrch(patientId, monthStr);
    const desiredCode = _desiredBaseCodeForMonth(raw, monthStr);
    if (forceRerun ||
        !existingNotes || !Object.keys(existingNotes).length ||
        billingNotesNeedRegeneration(existingNotes, desiredCode) ||
        !billingNotesMatchDesiredBaseCode(existingNotes, desiredCode)) {
      await generateAndSaveBillingNote(patient, monthStr, { painScaleOverride: opts.painScaleOverride === true });
      results.billingNoteGenerated = true;
    }
  } catch (e) {
    errors.push('billing note: ' + (e?.message || 'unknown error'));
    return { ok: false, results, errors, visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // ── Step 3: push billing note + phone note ──────────────────────────────
  if (onStep) onStep({ step: 'billing-push', patientId });
  const pushRes = await pushBillingNoteToElation(patient, monthStr, {
    _painScaleAcknowledged: opts._painScaleAcknowledged === true,
  });
  if (!pushRes.ok) {
    errors.push('billing push: ' + (pushRes.failures.join('; ') || 'unknown'));
    return { ok: false, results, errors, visitNoteIds: pushRes.visitNoteIds || [], nonVisitNoteIds: pushRes.nonVisitNoteIds || [] };
  }
  results.billingPushed = true;

  // Synthetic 'phone-push' step so the UI can show a 5-row progression even
  // though pushBillingNoteToElation already fired the phone note.
  if (onStep) onStep({ step: 'phone-push', patientId });
  const ph = pushRes.phoneNote;
  if (ph) {
    if (ph.ok) {
      // entries==0 means there was nothing to push, which is still a success
      // (matches v2 — pushMonthlyActivitiesAlongsideBillingNote returns
      // {pushed:0,failed:0,entries:0} in that case).
      results.phoneNotePushed = true;
    } else {
      // Non-fatal: the billing note already pushed. Record the error but
      // continue to billedState (v2 also records billed even when phone-note
      // push fails — see ccm-compliance.js:7405-7409).
      errors.push('phone note: ' + (ph.error || 'push failed'));
    }
  }

  // ── Step 4: billedState write ───────────────────────────────────────────
  if (onStep) onStep({ step: 'billed-state', patientId });
  try {
    await _markPatientBilled(patientId, monthStr, pushRes.servicedAt);
    results.billedStateUpdated = true;
  } catch (e) {
    errors.push('billed state: ' + (e?.message || 'save failed'));
    return { ok: false, results, errors, visitNoteIds: pushRes.visitNoteIds || [], nonVisitNoteIds: pushRes.nonVisitNoteIds || [] };
  }

  // Force a refetch on the next useDraft() call so the new plan/note show.
  invalidateDraftCache();
  // Propagate the Elation visit_note + non_visit_note ids back up so the
  // chrome-bar "Bulk Sign" and preview-header "Sign in Elation" buttons can
  // target them.
  return { ok: true, results, errors, visitNoteIds: pushRes.visitNoteIds || [], nonVisitNoteIds: pushRes.nonVisitNoteIds || [] };
}

// ── 99487 (Complex CCM) pre-generation review ──────────────────────────────
//
// CMS requires CPT 99487 to include establishment OR substantial revision of
// the comprehensive care plan per service period. When a 99487 patient has no
// new labs and no new office-visit notes since their prior plan, the
// substantial-revision element is not clearly met. The downgrade decision
// belongs in the workflow (here), not in the AI-generated chart text.
//
// check99487Eligibility returns:
//   null                                  — no warning (not 99487, first plan,
//                                            or has new clinical data)
//   { reason: 'no-new-data', sinceDate,
//     freshness }                          — fire the pre-generation modal
//
// Callers consume `freshness` two ways: (1) display sinceDate to the operator,
// (2) thread it through pushPatientToEmr via opts.freshness so the eventual
// generateAndSaveCarePlan call doesn't refetch the same data.
//
// `ctx` is the same passthrough getCarePlanFreshness accepts — pre-fetched
// cpHistory / latestNoteByEid for batched callers.
export async function check99487Eligibility(patient, monthStr, ctx) {
  const p = patient?._raw || patient || {};
  if ((p.billing || {}).program !== '99487') return null;
  const freshness = await getCarePlanFreshness(patient, monthStr, ctx);
  if (!freshness) return null;
  if (!freshness.hasPriorPlan) return null;   // first plan = "establishment"
  if (!freshness.noNewData) return null;      // has new data
  return { reason: 'no-new-data', sinceDate: freshness.sinceDate, freshness };
}

// Internal helpers for the batched variant — duplicate of the per-patient
// fetches inside careplan-generate.js, but call ccmApi.listCarePlans /
// latestVisitNotes ONCE for the whole batch instead of N times.
async function _prefetchAllCarePlanHistory() {
  try {
    const data = await ccmApi.listCarePlans();
    return (data && data.care_plan) ? data.care_plan : (data || {});
  } catch (_) { return {}; }
}
async function _prefetchLatestVisitNotes(elationIds) {
  if (!elationIds.length) return {};
  try {
    const data = await ccmApi.latestVisitNotes(elationIds);
    return (data && data.results) || {};
  } catch (_) { return {}; }
}

// Bulk variant for the push-modal pre-flight. Filters to 99487 patients only
// (cheap up-front check before any network), then prefetches shared data once
// and runs check99487Eligibility for each. Returns a Map<patientId, eligibility>
// containing only patients whose check fired (null results omitted).
export async function check99487EligibilityBatch(patients, monthStr) {
  const list = Array.isArray(patients) ? patients : [];
  const targets = list.filter(p => ((p?._raw || p)?.billing || {}).program === '99487');
  if (!targets.length) return new Map();
  const elationIds = Array.from(new Set(
    targets.map(p => (p?._raw || p)?.elation_id).filter(Boolean).map(String)
  ));
  const [cpHistory, latestNoteByEid] = await Promise.all([
    _prefetchAllCarePlanHistory(),
    _prefetchLatestVisitNotes(elationIds),
  ]);
  const out = new Map();
  for (const p of targets) {
    const elig = await check99487Eligibility(p, monthStr, { cpHistory, latestNoteByEid });
    if (elig) {
      const patientId = p?.id || (p?._raw || p)?.id;
      out.set(patientId, elig);
    }
  }
  return out;
}

// Bulk pre-flight for the tier-bump checklist. Filters to Standard-CCM (99490)
// patients with >= 90 CCM minutes and no APCM fallback this month, then attaches
// the advisory physician-time hint (one listStaff() fetch for the whole batch).
// Returns Map<patientId, { minutes, physicianPct, providerMin, existingCode }>
// — candidates only. `existingCode` is the patient's PRIOR same-month override
// from ccmTierMonth[monthStr] ('99487' | '99491' | null); the review modal
// seeds its radio state from it so a stale bump renders checked and an explicit
// down-select to "Keep 99490" clears it.
export async function checkTierBumpEligibilityBatch(patients, monthStr) {
  const list = Array.isArray(patients) ? patients : [];
  // Single pass: compute CCM minutes once per patient and keep it for the
  // candidates we surface (avoids a second activity-log traversal per patient).
  const candidates = [];
  for (const p of list) {
    const raw = (p && p._raw) || p || {};
    const minutes = ccmMonthlyMinutes(raw, monthStr);
    if (isTierBumpCandidate(raw, monthStr, minutes)) candidates.push({ p, raw, minutes });
  }
  if (!candidates.length) return new Map();
  let staffList = [];
  try { staffList = await ccmApi.listStaff(); } catch (_) { staffList = []; }
  const out = new Map();
  for (const { p, raw, minutes } of candidates) {
    const hint = physicianTimePct(raw, monthStr, staffList);
    const patientId = (p && p.id) || raw.id;
    // physicianPct is a 0–1 FRACTION (not a percentage). Consumers multiply by
    // 100 for display (see the bulk checklist modal + the affirmation audit line).
    const prior = (raw.ccmTierMonth && raw.ccmTierMonth[monthStr]) || null;
    out.set(patientId, {
      minutes,
      physicianPct: hint.pct,
      providerMin: hint.providerMin,
      existingCode: (prior && prior.code) ? String(prior.code) : null,
    });
  }
  return out;
}

// Persist an affirmation as a per-month ccmTierMonth override (top-level, sibling
// of apcmMonth — survives the staff-role billing-field guard) and audit it.
// `affirm` is { mdm?: bool, physicianTime?: bool }. Affirming nothing DELETES any
// prior override for the month (explicit un-affirm -> back to 99490). Returns the
// patient object with refreshed _raw/_updatedAt, or the original on no-op.
export async function writeCcmTierAffirmation(patient, monthStr, affirm, meta) {
  const p = (patient && patient._raw) || patient || {};
  const tier = resolveAffirmedTier(affirm);
  const existing = (p.ccmTierMonth && typeof p.ccmTierMonth === 'object') ? { ...p.ccmTierMonth } : {};
  const patientId = (patient && patient.id) || p.id;
  const m = meta || {};

  if (!tier) {
    if (!existing[monthStr]) return patient; // nothing affirmed, nothing stored
    delete existing[monthStr];
  } else {
    existing[monthStr] = {
      code: tier.code,
      basis: tier.basis,
      affirmedBy: m.affirmedBy || 'operator',
      affirmedAt: m.affirmedAt || new Date().toISOString(),
    };
  }

  const nextRaw = { ...p };
  if (Object.keys(existing).length) nextRaw.ccmTierMonth = existing;
  else delete nextRaw.ccmTierMonth;

  const resp = await ccmApi.updatePatient(patientId, nextRaw);
  const stamped = { ...nextRaw, _updatedAt: (resp && resp.updatedAt) || nextRaw._updatedAt };
  try {
    const detail = tier
      ? `${tier.basis} -> ${tier.code} for ${monthStr} (by ${m.affirmedBy || 'operator'}); ${m.minutes != null ? m.minutes : '?'} min, physicianPct ${m.physicianPct != null ? Math.round(m.physicianPct * 100) : '?'}%`
      : `tier bump cleared for ${monthStr} (by ${m.affirmedBy || 'operator'})`;
    await ccmApi.auditEvent('CCM_TIER_AFFIRMED', 'patients', patientId, detail);
  } catch (_) {}
  return { ...(patient || {}), _raw: stamped };
}

// Bulk variant — loops eligible patients sequentially. Eligibility is
// computed once against a freshly-fetched billedState (so racing pushes from
// another tab surface). onProgress fires before+after each patient.
//
// onProgress({ index, total, patientId, status, step?, error? }) where status ∈
//   { 'running', 'done', 'failed', 'skipped' }.
//
// opts.freshnessByPatientId — optional map of getCarePlanFreshness() results
// keyed by patient id. The push-modal pre-flight precomputes freshness for
// 99487-review patients; passing it through here lets generateAndSaveCarePlan
// skip the duplicate fetch.
//
// Returns the array of per-patient results in input order.
export async function pushAllToEmr(patients, monthStr, onProgress, opts) {
  _resetPushEmrSettingsCache();
  const cb = typeof onProgress === 'function' ? onProgress : () => {};
  const freshnessByPatientId = (opts && opts.freshnessByPatientId) || {};
  const total = patients.length;
  let billedState = {};
  try { billedState = await ccmApi.getBilledState(); } catch (_) {}
  const out = [];
  for (let i = 0; i < patients.length; i++) {
    const p = patients[i];
    const patientId = p?.id || p?._raw?.id;
    const elig = getPushEligibility(p, monthStr, billedState);
    if (!elig.eligible) {
      cb({ index: i, total, patientId, status: 'skipped', error: elig.reason });
      out.push({ patientId, ok: true, skipped: true, reason: elig.reason, results: {}, errors: [] });
      continue;
    }
    cb({ index: i, total, patientId, status: 'running' });
    try {
      const freshness = freshnessByPatientId[patientId];
      const res = await pushPatientToEmr(p, monthStr, {
        freshness: freshness || undefined,
        onStep: ({ step }) => cb({ index: i, total, patientId, status: 'running', step }),
      });
      if (res.ok && !res.skipped) {
        cb({ index: i, total, patientId, status: 'done' });
        // Update the local snapshot so subsequent eligibility checks in this
        // loop see this patient as already-billed.
        if (!billedState[monthStr]) billedState[monthStr] = {};
        billedState[monthStr][patientId] = { date: new Date().toISOString().slice(0, 10) };
      } else if (res.skipped) {
        cb({ index: i, total, patientId, status: 'skipped', error: res.reason });
      } else {
        // The Elation push can succeed even when the overall result is ok:false
        // (e.g. the billed-state save failed after the bill posted). When that
        // happens, mark the local snapshot billed so we never re-push the same
        // bill later in this run, and surface it as needs-verify rather than a
        // clean failure.
        if (res.results && res.results.billingPushed) {
          if (!billedState[monthStr]) billedState[monthStr] = {};
          billedState[monthStr][patientId] = { date: new Date().toISOString().slice(0, 10), needsVerify: true };
          cb({ index: i, total, patientId, status: 'failed', needsVerify: true, error: res.errors.join('; ') });
        } else {
          cb({ index: i, total, patientId, status: 'failed', error: res.errors.join('; ') });
        }
      }
      out.push({ patientId, ...res });
    } catch (e) {
      cb({ index: i, total, patientId, status: 'failed', error: e?.message || 'unknown error' });
      out.push({ patientId, ok: false, skipped: false, results: {}, errors: [e?.message || 'unknown error'] });
    }
  }
  return out;
}

// ── Two-stage workflow ────────────────────────────────────────────────────
//
// Replaces the single-shot Generate-and-Push pipeline above with two phases
// the provider can break between:
//   1. generateForPatient   — care plan + billing note in CCM only
//   2. (provider reviews)
//   3. pushAndSignForPatient — push billing+phone to Elation, PATCH-sign, mark billed
//
// The legacy pushPatientToEmr / pushAllToEmr above stay temporarily for any
// callers not yet migrated; new flows should use the two-stage pair.

// Stage 1: care plan + billing note generation. No Elation traffic.
// Honors a `forceRerun` flag — when true, regenerates the plan/note and
// (importantly) clears the patient's billedState entry so they reappear in
// the "ready to Push & Sign" bucket.
//
// BHI branch: when `opts.program === 'bhi'`, this skips the CCM eligibility +
// care-plan + CCM-billing-note pipeline and instead calls
// generateAndSaveBhiBillingNote (which has its own gate check via
// bhiGatesForCode). Returns the same `{ ok, results, errors }` shape so the
// orchestrator/UI doesn't have to special-case the BHI path.
export async function generateForPatient(patient, monthStr, options) {
  const opts = options || {};
  const program = (opts && opts.program) || 'ccm';
  const onStep = typeof opts.onStep === 'function' ? opts.onStep : null;
  const raw = patient?._raw || patient || {};
  const patientId = patient?.id || raw.id;

  // BHI branch — pure delegation to the BHI billing-note generator. No care
  // plan, no billed-state write (BHI billed-state tracking lands later).
  if (program === 'bhi') {
    const bhiResults = { carePlanGenerated: false, billingNoteGenerated: false };
    const bhiErrors = [];
    // Eligibility check via BHI gates rather than the CCM-shaped check.
    // opts.assessmentOverride demotes the missing-assessment blocker to a
    // warning (SOFT guard) so an explicitly-confirmed generate proceeds past
    // it. Enrollment/consent/minutes stay hard and still skip generation.
    const assessmentOverride = !!opts.assessmentOverride;
    const code = resolveBhiBillCode(patient, monthStr);
    const gates = bhiGatesForCode(raw, monthStr, code, { assessmentOverride });
    if (!gates.ok) {
      return {
        ok: true,
        skipped: true,
        reason: (gates.blockers && gates.blockers[0]) || 'BHI not eligible',
        results: bhiResults,
        errors: bhiErrors,
      };
    }
    // G0570 (APCM-paired month) folds into the CCM combined note; standalone
    // generation is a no-op for that case.
    if (code === 'G0570') {
      return {
        ok: true,
        skipped: true,
        reason: 'G0570 folds into APCM combined note; generate from CCM view',
        results: bhiResults,
        errors: bhiErrors,
      };
    }
    // Auto-build the comprehensive care plan if one doesn't already exist
    // for this month. CMS requires a behavioral health care plan on file
    // as a prerequisite for 99484 billing — and per Phase 6, BHI shares
    // the CCM care plan (no separate bhi_cp row). careplan-bhi.js appends
    // a <bhi_section> to the CCM prompt automatically when the patient
    // has billing.bhiProgram set, so the resulting plan covers BH topics.
    // This mirrors CCM's generateForPatient flow (care plan first, then
    // billing note) and ensures the BHI attestation can truthfully claim
    // a care plan is on file.
    try {
      const existingPlan = await _getMonthCarePlan(patientId, monthStr);
      if (!existingPlan) {
        if (onStep) onStep({ step: 'careplan', patientId });
        await generateAndSaveCarePlan(patient, monthStr);
        bhiResults.carePlanGenerated = true;
      }
    } catch (e) {
      bhiErrors.push('care plan (BHI auto-build): ' + (e?.message || 'unknown error'));
      return { ok: false, results: bhiResults, errors: bhiErrors };
    }

    if (onStep) onStep({ step: 'billing-note', patientId });
    try {
      await generateAndSaveBhiBillingNote(patient, monthStr, { assessmentOverride });
      bhiResults.billingNoteGenerated = true;
    } catch (e) {
      bhiErrors.push('bhi billing note: ' + (e?.message || 'unknown error'));
      return { ok: false, results: bhiResults, errors: bhiErrors };
    }
    // Clear any prior BHI billed-state mark so Regenerate restores Push & Sign
    // to the actionable state. Best-effort — no-op if not previously marked.
    try {
      let bs = {};
      try { bs = await ccmApi.getBilledState(); } catch (_) {}
      const wasBhiBilled = !!(bs && bs[monthStr] && bs[monthStr][patientId] && bs[monthStr][patientId].bhi_bn);
      if (wasBhiBilled) {
        await _unmarkPatientBilled(patientId, monthStr, { program: 'bhi' });
      }
    } catch (e) {
      bhiErrors.push('clear bhi billed: ' + (e?.message || 'unknown error'));
    }
    invalidateDraftCache();
    return { ok: true, results: bhiResults, errors: bhiErrors };
  }

  const results = {
    carePlanGenerated: false,
    billingNoteGenerated: false,
    billedStateCleared: false,
  };
  const errors = [];

  let billedState = {};
  try { billedState = await ccmApi.getBilledState(); } catch (_) {}
  // Eligibility check ignores "already submitted" — operator may regenerate
  // a billed-out month, which clears the billed flag below.
  const elig = getPushEligibility(patient, monthStr, billedState, { ignoreAlreadySubmitted: true });
  if (!elig.eligible) {
    return { ok: true, skipped: true, reason: elig.reason, results, errors };
  }

  const forceRerun = opts.forceRerun === true;
  const wasBilled = !!(billedState[monthStr] && billedState[monthStr][patientId]);
  if (forceRerun && wasBilled) {
    try {
      await _unmarkPatientBilled(patientId, monthStr);
      results.billedStateCleared = true;
    } catch (e) {
      errors.push('clear billed: ' + (e?.message || 'unknown error'));
    }
  }

  // Step 1: care plan
  if (onStep) onStep({ step: 'careplan', patientId });
  try {
    const existingPlan = await _getMonthCarePlan(patientId, monthStr);
    if (forceRerun || !existingPlan) {
      await generateAndSaveCarePlan(patient, monthStr,
        opts.freshness ? { freshness: opts.freshness } : undefined);
      results.carePlanGenerated = true;
    }
  } catch (e) {
    errors.push('care plan: ' + (e?.message || 'unknown error'));
    return { ok: false, results, errors };
  }

  // Step 2: billing note
  if (onStep) onStep({ step: 'billing-note', patientId });
  try {
    const existingNotes = await _getMonthBillingNotesOrch(patientId, monthStr);
    const desiredCode = _desiredBaseCodeForMonth(raw, monthStr);
    if (forceRerun ||
        !existingNotes || !Object.keys(existingNotes).length ||
        billingNotesNeedRegeneration(existingNotes, desiredCode) ||
        !billingNotesMatchDesiredBaseCode(existingNotes, desiredCode)) {
      await generateAndSaveBillingNote(patient, monthStr, { painScaleOverride: opts.painScaleOverride === true });
      results.billingNoteGenerated = true;
    }
  } catch (e) {
    errors.push('billing note: ' + (e?.message || 'unknown error'));
    return { ok: false, results, errors };
  }

  invalidateDraftCache();
  return { ok: true, results, errors };
}

// CMS2 depression-screening (PHQ-9/PHQ-2) helper. Returns the in-month PHQ
// assessment to file a screening report for, or null when there's nothing to
// file. Null when:
//   - the patient already has a CMS2 report stamped for this month (dedup), or
//   - there's no PHQ-9/PHQ-2 assessment dated in `monthStr`.
// Dual patient shape: /data/patients returns FLAT blobs ({id,bhi,...}); studio's
// adaptPatient wraps with _raw. `patient._raw || patient` reads either shape,
// matching how every other reader in this file unwraps the patient. The
// server route (push-cqm-screening) re-derives this from the chart row, so this
// is a cheap client-side guard against an obviously-pointless network call, not
// the authority.
function _phqAssessmentForMonth(patient, monthStr) {
  const raw = (patient && (patient._raw || patient)) || {};
  const list = (raw.bhi && Array.isArray(raw.bhi.assessments)) ? raw.bhi.assessments : [];
  const already = raw.cms2Reported && raw.cms2Reported[monthStr];
  if (already) return null;
  return list.find((a) => a
    && (a.scale === 'PHQ-9' || a.scale === 'PHQ-2')
    && String(a.date || '').slice(0, 7) === monthStr) || null;
}

// ── CMS2 screening shared helpers ────────────────────────────────────────────
// The CCM and BHI push paths both file+sign+stamp a CMS2 depression-screening
// report when an in-month PHQ-9/PHQ-2 was logged. These three helpers hold the
// shared logic so the two paths cannot drift. All are BEST-EFFORT: a screening
// push/sign/marker failure is recorded as a non-fatal error but never changes
// the surrounding push's success/failure contract.

// File (push) the CMS2 screening report when the patient has an in-month PHQ
// that hasn't already been CMS2-reported. Returns the Elation report id to
// thread into the sign batch, or null when there's nothing to file (or the
// push failed — recorded non-fatally in `errors`). `raw = patient._raw||patient`.
async function _pushCms2Screening(patient, monthStr, physicianId, { onStep, patientId, results, errors }) {
  if (!_phqAssessmentForMonth(patient, monthStr)) return null;
  const raw = (patient && (patient._raw || patient)) || {};
  try {
    if (onStep) onStep({ step: 'cqm-screening', patientId });
    const scr = await ccmApi.pushCqmScreening({ elationId: raw.elation_id, month: monthStr, physicianId });
    results.cqmScreeningPushed = !!(scr && (scr.reportId || scr.skipped));
    return (scr && scr.reportId) || null;
  } catch (e) {
    // Non-fatal — must not block the care-plan/billing push.
    errors.push('cqm-screening: ' + (e?.message || 'screening push failed'));
    return null;
  }
}

// Inspect a _signWithRetry result against the screening report's leg.
//   - screeningSignedOk: the report leg (matched by reportId) came back ok, OR
//     its error is an "already signed"-class error. _signWithRetry retries the
//     WHOLE payload on a transient error, so if the first attempt actually
//     signed the report, the retry's report leg returns a false failure — treat
//     that as success for the dedup-stamp decision.
//   - realFailures: every leg (note + non-visit + report) that genuinely failed
//     (!ok with an error that is NOT the tolerated already-signed class).
function _evalScreeningSign(signResult, screeningReportId) {
  const reportLeg = (signResult && signResult.reportResults || [])
    .find((r) => r && r.reportId === screeningReportId);
  let screeningSignedOk = false;
  if (screeningReportId && reportLeg) {
    screeningSignedOk = reportLeg.ok || /already.*signed|cannot sign/i.test(String(reportLeg.error || ''));
  }
  const realFailures = [...(signResult && signResult.results || []), ...(signResult && signResult.nonVisitResults || []), ...(signResult && signResult.reportResults || [])]
    .filter((r) => r && !r.ok && r.error && !/already.*signed|cannot sign/i.test(String(r.error)));
  return { screeningSignedOk, realFailures };
}

// Stamp the program-agnostic CMS2 dedup marker (cms2Reported[month]) into the
// patient blob so the next push (either program) sees it via
// _phqAssessmentForMonth and skips re-filing. Re-fetches the patient first so
// the full-blob PUT writes against FRESH server state: the billing push above
// bumped patients.updated_at server-side, so the in-memory snapshot's
// _updatedAt is stale and a PUT with it would 409 on every first run (marker
// never persists → next month re-files). Falls back to `fallbackRaw` if the
// re-fetch fails. Best-effort: a save failure is recorded but never fails push.
async function _stampCms2Marker(patientId, fallbackRaw, monthStr, screeningReportId, { results, errors }) {
  try {
    let markRaw = fallbackRaw;
    try {
      const fresh = await ccmApi.getPatient(patientId);
      if (fresh && fresh.id) markRaw = fresh;
    } catch (_) { /* fall back to the snapshot — server re-derives, so no dup-report risk */ }
    const nextRaw = {
      ...markRaw,
      cms2Reported: {
        ...(markRaw.cms2Reported && typeof markRaw.cms2Reported === 'object' ? markRaw.cms2Reported : {}),
        [monthStr]: { reportId: screeningReportId, at: new Date().toISOString() },
      },
    };
    await ccmApi.updatePatient(patientId, nextRaw);
    results.cqmScreeningReported = true;
  } catch (e) {
    errors.push('cqm-screening mark: ' + (e?.message || 'save failed'));
  }
}

// Stage 2: ship a previously-generated month to Elation, PATCH-sign, mark
// billed. Atomic from the operator's POV (sign retries once on failure).
// Requires a saved billing note for (patient, month) — otherwise it short-
// circuits with a "Generate first" reason.
//
// BHI branch: when `opts.program === 'bhi'`:
//   - If the resolved code is G0570 (APCM-paired month), return a no-op result
//     — the G0570 add-on rides on the CCM combined note, so pushing from the
//     BHI view is forbidden.
//   - Otherwise (99484 standalone), push the BHI billing note (read from
//     BHI_BN_KEY) as a separate visit note, then sign. Uses the same Elation
//     endpoint as the CCM push but with the BHI note body + 99484 CPT.
export async function pushAndSignForPatient(patient, monthStr, options) {
  const opts = options || {};
  const program = (opts && opts.program) || 'ccm';
  const onStep = typeof opts.onStep === 'function' ? opts.onStep : null;
  const raw = patient?._raw || patient || {};
  const patientId = patient?.id || raw.id;

  // BHI branch — separate visit note push. Thread the assessment-override
  // acknowledgement so the push leg can audit a no-assessment override.
  if (program === 'bhi') {
    return await _pushAndSignForPatientBhi(patient, monthStr, { onStep, assessmentOverride: !!opts.assessmentOverride });
  }

  const results = {
    billingPushed: false,
    phoneNotePushed: false,
    signed: false,
    billedStateUpdated: false,
  };
  const errors = [];

  // Hard preconditions: linked, not already billed, has a generated billing note.
  let billedState = {};
  try { billedState = await ccmApi.getBilledState(); } catch (_) {}
  if (billedState[monthStr] && billedState[monthStr][patientId]) {
    return { ok: true, skipped: true, reason: 'already submitted', results, errors, visitNoteIds: [], nonVisitNoteIds: [] };
  }
  if (!raw.elation_id) {
    return { ok: false, results, errors: ['Patient is not linked to Elation.'], visitNoteIds: [], nonVisitNoteIds: [] };
  }
  const existingNotes = await _getMonthBillingNotesOrch(patientId, monthStr);
  if (!existingNotes || !Object.keys(existingNotes).length) {
    return { ok: false, results, errors: ['No billing note generated for this month — click Generate first.'], visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // Push billing note + phone note via the existing pipeline.
  if (onStep) onStep({ step: 'billing-push', patientId });
  const pushRes = await pushBillingNoteToElation(patient, monthStr, {
    _painScaleAcknowledged: opts._painScaleAcknowledged === true,
  });
  if (!pushRes.ok) {
    errors.push('billing push: ' + (pushRes.failures.join('; ') || 'unknown'));
    return { ok: false, results, errors, visitNoteIds: pushRes.visitNoteIds || [], nonVisitNoteIds: pushRes.nonVisitNoteIds || [] };
  }
  results.billingPushed = true;

  if (onStep) onStep({ step: 'phone-push', patientId });
  if (pushRes.phoneNote) {
    if (pushRes.phoneNote.ok) {
      results.phoneNotePushed = true;
    } else {
      errors.push('phone note: ' + (pushRes.phoneNote.error || 'push failed'));
    }
  }

  // CMS2 depression-screening report (best-effort). When the patient has an
  // in-month PHQ-9/PHQ-2 assessment that hasn't already been CMS2-reported,
  // file an unsigned Elation Screening report so the logged PHQ hits the CMS2
  // MIPS numerator, then sign it in the same batch as the billing note below.
  // ANY failure here (push or, later, sign) must leave the care-plan/billing
  // flow result exactly as it would have been without screening — the screening
  // report is an additive quality signal, not part of the billable deliverable.
  const { physicianId } = await _getSettings();
  const screeningReportId = await _pushCms2Screening(patient, monthStr, physicianId, { onStep, patientId, results, errors });

  // Sign the freshly-pushed notes (and the screening report, if filed). Failures
  // are recorded but don't abort — the push already succeeded; an unsigned draft
  // sitting in the Elation inbox is degraded UX, not data loss.
  let screeningSignedOk = false;
  if (onStep) onStep({ step: 'sign', patientId });
  try {
    const signResult = await _signWithRetry({
      noteIds: pushRes.visitNoteIds || [],
      nonVisitNoteIds: pushRes.nonVisitNoteIds || [],
      reportIds: screeningReportId ? [screeningReportId] : [],
    });
    // Report-leg-aware: tolerate the already-signed false-failure on retry and
    // count only genuine sign failures (see _evalScreeningSign).
    const { screeningSignedOk: ok, realFailures } = _evalScreeningSign(signResult, screeningReportId);
    screeningSignedOk = ok;
    if (realFailures.length) {
      errors.push('sign: ' + (realFailures[0].error || `${realFailures.length} failed`));
    } else {
      results.signed = true;
    }
  } catch (e) {
    errors.push('sign: ' + (e?.message || 'sign request failed'));
  }

  // Stamp the CMS2 dedup marker ONLY when the screening report was both filed
  // AND signed (or tolerated-already-signed). Stored in the patient blob under
  // cms2Reported[month] — the same store _phqAssessmentForMonth reads to dedup.
  if (screeningReportId && screeningSignedOk) {
    await _stampCms2Marker(patientId, raw, monthStr, screeningReportId, { results, errors });
  }

  // Mark billed regardless of sign outcome — the push made it to Elation.
  if (onStep) onStep({ step: 'billed-state', patientId });
  try {
    await _markPatientBilled(patientId, monthStr, pushRes.servicedAt);
    results.billedStateUpdated = true;
  } catch (e) {
    errors.push('billed state: ' + (e?.message || 'save failed'));
    return { ok: false, results, errors, visitNoteIds: pushRes.visitNoteIds || [], nonVisitNoteIds: pushRes.nonVisitNoteIds || [] };
  }

  return {
    ok: errors.length === 0 || (results.billingPushed && results.billedStateUpdated),
    results,
    errors,
    visitNoteIds: pushRes.visitNoteIds || [],
    nonVisitNoteIds: pushRes.nonVisitNoteIds || [],
    referralsDrafted: pushRes.referralsDrafted || 0,
  };
}

// BHI variant of pushAndSignForPatient. Pushes the standalone 99484 visit
// note (read from BHI_BN_KEY in the careplans store) to Elation via the same
// /data/elation/push-careplan endpoint the CCM path uses, then pushes a
// separate BHI consolidated phone note (BHI-tagged entries only — the
// program-aware filter in pushMonthForPatient keeps CCM and BHI activity
// in separate Elation chart entries), then signs both. Writes the
// billing-state mark with `program: 'bhi'` so the preview Push & Sign
// button disables post-sign.
//
// G0570 (APCM-paired month): no-op skip. The G0570 add-on rides on the CCM
// combined note (see Part B of Task 1.12: getCcmBillingCodes now appends
// G0570 to the bill payload when BHI gates pass on an APCM month).
async function _pushAndSignForPatientBhi(patient, monthStr, ctx) {
  const onStep = ctx && ctx.onStep;
  const raw = patient?._raw || patient || {};
  const patientId = patient?.id || raw.id;
  const results = {
    billingPushed: false,
    phoneNotePushed: false,
    signed: false,
    billedStateUpdated: false,
  };
  const errors = [];

  if (!raw.elation_id) {
    return { ok: false, results, errors: ['Patient is not linked to Elation.'], visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // Already-submitted precondition — mirrors the CCM path, but keyed on the
  // BHI slot (bhi_bn) specifically so a CCM-billed dual patient isn't wrongly
  // skipped for BHI. Without this, a re-entry after the slot is marked would
  // double-push the 99484.
  let billedState = {};
  try { billedState = await ccmApi.getBilledState(); } catch (_) {}
  if (billedState[monthStr] && billedState[monthStr][patientId] && billedState[monthStr][patientId][BHI_BN_KEY]) {
    return { ok: true, skipped: true, reason: 'already submitted', results, errors, visitNoteIds: [], nonVisitNoteIds: [] };
  }

  const code = resolveBhiBillCode(patient, monthStr);
  if (code === 'G0570') {
    return {
      ok: true,
      skipped: true,
      reason: 'G0570 folds into APCM combined note; push from CCM view',
      results,
      errors,
      visitNoteIds: [],
      nonVisitNoteIds: [],
    };
  }

  // ENFORCE the hard gates at the push leg — not just at generate. The
  // assessment-override soft guard demotes ONLY the missing-assessment reason
  // to a warning; enrollment, consent, and the 20-minute floor stay HARD and
  // must abort the push exactly as they abort generation. (Without this, a
  // note generated under a since-lost consent — or a re-entry after minutes
  // dropped — could still ship.) The override value is the operator's explicit
  // acknowledgement threaded from the confirmation modal.
  const assessmentOverride = !!(ctx && ctx.assessmentOverride);
  const gates = bhiGatesForCode(raw, monthStr, code, { assessmentOverride });
  if (!gates.ok) {
    return { ok: false, results, errors: gates.blockers || ['BHI not billable'], visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // AUDIT the assessment override on the push leg too: the operator explicitly
  // chose to ship a billed 99484 note with no initial rating-scale assessment
  // on file. Only fires when the override actually suppressed a real
  // missing-assessment warning for this month.
  if (assessmentOverride && (gates.warnings || []).includes('Initial assessment missing')) {
    safeAudit('BHI_NO_ASSESSMENT_OVERRIDE', patientId,
      'BHI 99484 note pushed to Elation for ' + monthStr + ' with the initial rating-scale assessment gate overridden — no PHQ-9/PHQ-2 assessment on file for the period. Operator explicitly proceeded.'
    ).catch(() => {});
  }

  // Pull the saved BHI billing note from the careplans store. Stored under
  // BHI_BN_KEY (= 'bhi_bn'), distinct from BN_COMBINED_KEY (= 'bn_combined').
  const monthNotes = await _getMonthBillingNotesOrch(patientId, monthStr);
  const bhiNoteText = monthNotes && monthNotes[BHI_BN_KEY];
  if (!bhiNoteText) {
    return { ok: false, results, errors: ['No BHI billing note generated for this month — click Generate first.'], visitNoteIds: [], nonVisitNoteIds: [] };
  }

  // Construct the push job. Payload shape mirrors the CCM combined-note push
  // (one job per push, single CPT in the cpts[] array). The Elation visit-
  // note endpoint is the same; the server stamps it based on the cpts shape.
  const name = (raw.fname || '') + ' ' + (raw.lname || '');
  const rateInfo = { desc: RATE_DESCS['99484'] || 'BHI Care Mgmt, per calendar month' };
  const cptsForBill = [{ cptCode: '99484', cptDescription: rateInfo.desc, units: 1 }];

  // Service date — reuse CCM's resolver so BHI bills land on the same date
  // the practice picked in settings (last day of month by default).
  const { elationBilling, physicianId } = await _getSettings();
  const servicedAt = await getElationBillingServiceDate(monthStr, elationBilling);
  if (!servicedAt) {
    return { ok: false, results, errors: ['service date not selected (custom_prompt cancelled)'], visitNoteIds: [], nonVisitNoteIds: [] };
  }

  if (onStep) onStep({ step: 'billing-push', patientId });
  const job = {
    elationId: raw.elation_id,
    planText: bhiNoteText,
    month: monthStr,
    label: name + ' (99484)',
    cpts: cptsForBill,
    servicedAt,
    program: 'bhi',
  };
  const pushRes = await _pushManyToElation([job], physicianId);
  const visitNoteIds = pushRes.jobResults.filter((r) => r.ok && r.visitNoteId).map((r) => r.visitNoteId);
  if (pushRes.pushed !== 1) {
    errors.push('billing push: ' + (pushRes.failures.join('; ') || 'unknown'));
    return { ok: false, results, errors, visitNoteIds, nonVisitNoteIds: [] };
  }
  results.billingPushed = true;

  // Push a separate BHI consolidated phone note (program-aware filter
  // includes only BHI-tagged entries — no leakage into the next CCM phone
  // note, and the title says "BHI monthly time summary"). The phone note
  // anchors on the same chart date as the visit note so the chart audit
  // pairs them.
  let nonVisitNoteIds = [];
  // Re-fetch the patient so the phone-note push writes against FRESH server
  // state. The billing push above (and, for a dual CCM+BHI patient, the CCM
  // task that ran before this one) bumped patients.updated_at server-side via
  // the push-careplan `billingPush` idempotency claim, so the in-memory
  // snapshot's _updatedAt is now stale. pushMonthForPatient does a full-blob
  // PUT; with the stale token it would 409 ("patient was modified by another
  // user") AFTER the note already pushed+signed, and forcing it through would
  // clobber the server-written billingPush marker (re-opening the duplicate-
  // bill door). Re-fetching yields a current token AND the current blob.
  let phonePatient = patient;
  try {
    const fresh = await ccmApi.getPatient(patientId);
    if (fresh && fresh.id) phonePatient = { ...patient, id: patientId, _raw: fresh };
  } catch (_) { /* fall back to the snapshot — the bill is already safe; the phone note may 409 */ }
  if (onStep) onStep({ step: 'phone-push', patientId });
  try {
    const phoneRes = await pushMonthForPatient(phonePatient, monthStr, { alignDate: servicedAt, program: 'bhi' });
    if (phoneRes && phoneRes.ok) {
      results.phoneNotePushed = true;
      // Only push a real Elation non_visit_note id into the sign batch
      // (the server returns 'created' as a placeholder when it can't
      // determine an integer id — that placeholder isn't sign-able).
      if (Number.isInteger(phoneRes.noteId) && phoneRes.noteId > 0) {
        nonVisitNoteIds.push(phoneRes.noteId);
      }
    } else if (phoneRes && phoneRes.error) {
      errors.push('bhi phone note: ' + phoneRes.error);
    }
  } catch (e) {
    errors.push('bhi phone note: ' + (e?.message || 'unknown error'));
  }

  // CMS2 depression-screening report (best-effort, mirrors the CCM path). A
  // BHI 99484 visit note signed via push-and-sign qualifies as the CMS2 eCQM
  // encounter just like the CCM note, so a BHI-only patient whose PHQ-9 was
  // logged this month must still get a screening report filed+signed. The
  // cms2Reported[month] marker is program-agnostic and shared: for a dual
  // CCM+BHI patient pushed through both paths, whichever runs first files the
  // report + stamps the marker, and the second path's _phqAssessmentForMonth
  // sees the marker → returns null → skips (no double-file). physicianId is in
  // scope from _getSettings() above.
  const screeningReportId = await _pushCms2Screening(patient, monthStr, physicianId, { onStep, patientId, results, errors });

  // Sign the freshly-pushed BHI visit note + (if pushed) the BHI phone
  // note + (if filed) the screening report in one batch, mirroring the CCM
  // sign step.
  let screeningSignedOk = false;
  if (onStep) onStep({ step: 'sign', patientId });
  try {
    const signResult = await _signWithRetry({
      noteIds: visitNoteIds,
      nonVisitNoteIds,
      reportIds: screeningReportId ? [screeningReportId] : [],
    });
    // Report-leg-aware (mirrors the CCM path): tolerate the already-signed
    // false-failure on retry and count only genuine sign failures, so a
    // tolerated report leg can't surface as a false sign error and we get the
    // screeningSignedOk flag for the marker decision below.
    const { screeningSignedOk: ok, realFailures } = _evalScreeningSign(signResult, screeningReportId);
    screeningSignedOk = ok;
    if (realFailures.length) {
      errors.push('sign: ' + (realFailures[0].error || `${realFailures.length} failed`));
    } else {
      results.signed = true;
    }
  } catch (e) {
    errors.push('sign: ' + (e?.message || 'sign request failed'));
  }

  // Stamp the shared CMS2 dedup marker ONLY when the screening report was both
  // filed AND signed (or tolerated-already-signed). Best-effort: the helper
  // re-fetches the patient (fresh token) and never fails the push. `raw` is the
  // page-load snapshot fallback.
  if (screeningReportId && screeningSignedOk) {
    await _stampCms2Marker(patientId, raw, monthStr, screeningReportId, { results, errors });
  }

  // Mark the BHI slot as billed on PUSH success — not sign success — matching
  // the CCM path ("the push made it to Elation"). The 99484 bill/note are
  // already in Elation after the push; gating the billed mark on signing would
  // leave a push-ok/sign-fail month re-pushable and double-bill 99484. An
  // unsigned draft in the inbox is degraded UX (recoverable via Bulk Sign),
  // not a duplicate bill. Best-effort — a saveBilledState failure is recorded
  // but doesn't fail the overall push.
  if (results.billingPushed) {
    try {
      await _markPatientBilled(patientId, monthStr, servicedAt, { program: 'bhi' });
      results.billedStateUpdated = true;
    } catch (e) {
      errors.push('mark billed: ' + (e?.message || 'unknown error'));
    }
  }

  return {
    // Parity with the CCM path: a non-fatal secondary error (e.g. the phone-note
    // time-summary) must not report the whole push as failed once the billing
    // note is in Elation and the slot is marked billed.
    ok: errors.length === 0 || (results.billingPushed && results.billedStateUpdated),
    results,
    errors,
    visitNoteIds,
    nonVisitNoteIds,
  };
}

// Bulk variants used by the multi-stage push modal. Both follow the same
// onProgress contract as pushAllToEmr above.
export async function generateAllForEmr(patients, monthStr, onProgress, opts) {
  _resetPushEmrSettingsCache();
  const cb = typeof onProgress === 'function' ? onProgress : () => {};
  const freshnessByPatientId = (opts && opts.freshnessByPatientId) || {};
  const total = patients.length;
  const out = [];
  for (let i = 0; i < patients.length; i++) {
    const p = patients[i];
    const patientId = p?.id || p?._raw?.id;
    cb({ index: i, total, patientId, status: 'running' });
    try {
      const res = await generateForPatient(p, monthStr, {
        freshness: freshnessByPatientId[patientId] || undefined,
        forceRerun: !!(opts && opts.forceRerun),
        onStep: ({ step }) => cb({ index: i, total, patientId, status: 'running', step }),
      });
      if (res.skipped) {
        cb({ index: i, total, patientId, status: 'skipped', error: res.reason });
      } else if (res.ok) {
        cb({ index: i, total, patientId, status: 'done' });
      } else {
        cb({ index: i, total, patientId, status: 'failed', error: res.errors.join('; ') });
      }
      out.push({ patientId, ...res });
    } catch (e) {
      cb({ index: i, total, patientId, status: 'failed', error: e?.message || 'unknown error' });
      out.push({ patientId, ok: false, results: {}, errors: [e?.message || 'unknown error'] });
    }
  }
  return out;
}

export async function pushAndSignAllForEmr(patients, monthStr, onProgress, opts) {
  _resetPushEmrSettingsCache();
  const cb = typeof onProgress === 'function' ? onProgress : () => {};
  const total = patients.length;
  const out = [];
  for (let i = 0; i < patients.length; i++) {
    const p = patients[i];
    const patientId = p?.id || p?._raw?.id;
    cb({ index: i, total, patientId, status: 'running' });
    try {
      const res = await pushAndSignForPatient(p, monthStr, {
        onStep: ({ step }) => cb({ index: i, total, patientId, status: 'running', step }),
      });
      if (res.skipped) {
        cb({ index: i, total, patientId, status: 'skipped', error: res.reason });
      } else if (res.ok) {
        cb({ index: i, total, patientId, status: 'done' });
      } else {
        cb({ index: i, total, patientId, status: 'failed', error: res.errors.join('; ') });
      }
      out.push({ patientId, ...res });
    } catch (e) {
      cb({ index: i, total, patientId, status: 'failed', error: e?.message || 'unknown error' });
      out.push({ patientId, ok: false, results: {}, errors: [e?.message || 'unknown error'], visitNoteIds: [], nonVisitNoteIds: [] });
    }
  }
  return out;
}

// Helper used by the chrome bar + preview header to count what action the
// operator can take next. Pure — no network. Returns { needsGenerate,
// needsPushSign, isBilled } based on existing billing-notes presence + billed
// state. `existingBillingNotes` is the same shape ccmApi.listBillingNotes()
// returns (keyed by patientId then by monthStr).
//
// opts.program === 'bhi': checks the BHI billing-note key ('bhi_bn') instead
// of the CCM keys so a dual-enrolled patient who has only generated the CCM
// note doesn't incorrectly show the BHI Push & Sign button as enabled.
export function getWorkflowState(patient, monthStr, billedState, existingBillingNotes, opts) {
  const raw = patient?._raw || patient || {};
  const patientId = patient?.id || raw.id;
  const program = (opts && opts.program) || 'ccm';
  const bn = existingBillingNotes && existingBillingNotes[patientId] && existingBillingNotes[patientId][monthStr];

  let hasBillingNote, isBilled;
  if (program === 'bhi') {
    // BHI: only the 'bhi_bn' key counts as a generated BHI billing note.
    hasBillingNote = !!(bn && typeof bn === 'object' && bn[BHI_BN_KEY]);
    // BHI billed-state tracking is phase-gated; use the same billedState map
    // but keyed by 'bhi_bn' entry. Until the BHI billed-state phase lands,
    // isBilled will always be false here (no 'bhi_bn' entry exists in the map).
    isBilled = !!(billedState && billedState[monthStr] && billedState[monthStr][patientId] && billedState[monthStr][patientId]['bhi_bn']);
  } else {
    // CCM/APCM: a non-empty billing-notes map counts as generated — but the
    // BHI key (bhi_bn) is NOT a CCM note. A bhi_bn-only map must read as
    // "no CCM note generated" so the CCM push path never ships the BHI note as
    // a CPT-less care-plan note and marks the month CCM-billed.
    isBilled = !!(billedState && billedState[monthStr] && billedState[monthStr][patientId]);
    hasBillingNote = !!(bn && (typeof bn === 'string' ? bn.trim() : Object.keys(bn).some((k) => k !== BHI_BN_KEY)));
  }

  // "Generate" is the next action when the month hasn't been generated yet
  // or when it's been billed (so the operator's only re-entry is Regenerate).
  const needsGenerate = !hasBillingNote;
  // "Push & Sign" is the next action when a generated note is sitting unpushed.
  const needsPushSign = hasBillingNote && !isBilled;
  return { needsGenerate, needsPushSign, isBilled, hasBillingNote };
}
